<template>
  <div
    class="
      tw-flex
      tw-items-end
      tw-gap-3
      tw-min-h-[48px]
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-border-sf-slate-lighter-2
      tw-bg-white
      tw-px-3
      tw-py-[9px]
      tw-shadow-[0_4px_12px_0_rgba(0,0,0,0.08)]
      focus-within:tw-border-sf-primary
      focus-within:tw-shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-sky-default,#06b6d4)_15%,transparent),0_4px_12px_0_rgba(0,0,0,0.08)]
    "
  >
    <textarea
      ref="textareaRef"
      :value="modelValue"
      :placeholder="placeholder ?? defaultPlaceholder"
      rows="1"
      class="
        assistant-input__field
        tw-min-w-0 tw-flex-1 tw-resize-none tw-appearance-none tw-font-sans
        !tw-mx-0
        !tw-my-auto
        !tw-p-0
        !tw-border-0
        !tw-bg-transparent
        !tw-shadow-none
        focus:!tw-shadow-none
        !tw-outline-none
        focus:!tw-outline-none
        tw-text-sm tw-text-sf-slate-darker-1
        placeholder:tw-text-sf-slate-light-2
        placeholder:!tw-not-italic
      "
      data-testid="assistant-input"
      @input="onInput"
      @keydown="onKeydown"
    ></textarea>

    <SfButtonDs
      v-if="loading"
      variant="tertiary"
      size="md"
      icon-only
      icon-before="stop"
      :aria-label="I18n.t('companies_overview.assistant.stop')"
      data-testid="assistant-stop"
      @click="$emit('cancel')"
    />
    <SfButtonDs
      v-else
      variant="tertiary"
      size="md"
      icon-only
      :icon-before="submitIcon"
      :disabled="!canSubmit"
      :aria-label="I18n.t('companies_overview.assistant.submit')"
      data-testid="assistant-submit"
      @click="submit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import I18n from '@app/adapters/I18n'
import { SfButtonDs } from '@silverfin/ui-components'

// Cap the auto-growing textarea before it scrolls internally.
const MAX_INPUT_HEIGHT = 160

interface Props {
  modelValue: string
  loading?: boolean
  placeholder?: string
  autofocus?: boolean
  submitIcon?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  placeholder: undefined,
  autofocus: true,
  submitIcon: 'arrow-up',
})

const emit =
  defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'submit'): void
    (e: 'cancel'): void
  }>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)

const defaultPlaceholder = computed(() =>
  props.loading
    ? I18n.t('companies_overview.assistant.input_placeholder_loading')
    : I18n.t('companies_overview.assistant.input_placeholder')
)

const canSubmit = computed(
  () => props.modelValue.trim().length > 0 && !props.loading
)

function resize(): void {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`
  el.style.overflowY = el.scrollHeight > MAX_INPUT_HEIGHT ? 'auto' : 'hidden'
}

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLTextAreaElement).value)
  void nextTick(resize)
}

function submit(): void {
  if (!canSubmit.value) return
  emit('submit')
}

function onKeydown(event: KeyboardEvent): void {
  // An IME commits its candidate with Enter. Submitting here would send a half-typed
  // prompt and swallow the candidate.
  if (event.isComposing) return
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    submit()
  }
}

function focus(): void {
  textareaRef.value?.focus()
}

watch(
  () => props.modelValue,
  (value, previous) => {
    void nextTick(resize)
    // Text arriving from outside the input (a suggestion pill) should land in a focused field.
    if (value && !previous) focus()
  }
)

onMounted(() => {
  // The field is only focusable once its container is in the document.
  if (props.autofocus) void nextTick(focus)
  resize()
})
</script>
